import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CTabContent,
  CTabPane,
  CTabs,
  CNav,
  CNavItem,
  CNavLink,
  CTextarea,
  CDataTable,
} from "@coreui/react";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getList,
  getItem,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, warningAlert } from "../utils/messages";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import ProductSelect from "../product/ProductSelect";
import ProductionOrder, {
  ProductionOrderItem,
  ProductionOrderItemLog,
  ProductionOrderSize,
  newProductionOrder,
} from "../../models/production-order";
import Product from "../../models/product";
import ProductionInputMaterialForm from "./ProductionMaterialInputForm";
import ProductionLaborPriceForm from "./ProductionLaborPriceForm";
import {
  UniqueListItem,
  listToUniqueListItems,
  newUniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import ProductionOrderLaborPrice, {
  newProductionOrderLaborPrice,
} from "../../models/production-labor-price";
import ProductionOrderInputMaterial from "../../models/production-input-material";
import ProductionStage from "../../models/production-stage";
import CurrencyField from "../currencies/CurrencyField";
import { PYG } from "../../currency/available-currencies";
import { orderIsEditable } from "../../utils/production-orders";
import {
  VariantTableEntry,
  productionOrderItemAPIToTable,
} from "../../models/variant";
import {
  defaultValueOnUndefined,
  emptyValueOnUndefined,
} from "../../utils/fields";
import { formatDateTime } from "../../utils/dates";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import InventoryInputBatch from "../../models/inventory-input-batch";
import { getCommittedPendingByBatch } from "../../api/production-materials";
import { withBrand } from "../../utils/brands";

interface ProductionFormProps {
  initialProductionOrder?: ProductionOrder;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const FIXED_COLUMNS = ["Color"];

const ProductionForm: React.FC<ProductionFormProps> = ({
  initialProductionOrder,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const fields = [
    {
      key: "createdAt",
      _classes: ["text-center"],
      label: "Fecha",
    },
    {
      key: "productName",
      _classes: ["text-center"],
      label: "Item/Variante",
    },
    {
      key: "registerType",
      _classes: ["text-center"],
      label: "Tipo de registro",
    },
    {
      key: "amount",
      _classes: ["text-center"],
      label: "Cantidad",
    },
    {
      key: "user",
      _classes: ["text-center"],
      label: "Usuario",
    },
  ];

  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingOrder, setEditingOrder] = useState<ProductionOrder>(
    initialProductionOrder ? initialProductionOrder : {},
  );
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {},
  );
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editable, setEditable] = useState(true);
  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [totalCostPrice, setTotalCostPrice] = useState<number>(0);
  const [expectedProfit, setExpectedProfit] = useState<number>(0);
  const [expectedProfitWholesale, setExpectedProfitWholesale] =
    useState<number>(0);
  const [expectedProfitSuperWholesale, setExpectedProfitSuperWholesale] =
    useState<number>(0);

  const [tableColumns, setTableColumns] = useState<string[]>(FIXED_COLUMNS);

  const [tableEntries, setTableEntries] = useState<VariantTableEntry[]>([]);

  const [laborPrices, setLaborPrices] = useState<
    UniqueListItem<ProductionOrderLaborPrice>[]
  >([]);

  const [itemLogs, setItemLogs] = useState<ProductionOrderItemLog[]>([]);

  const [productionOrderSizes, setProductionOrderSizes] = useState<
    ProductionOrderSize[]
  >([]);

  const [inputMaterials, setInputMaterials] = useState<
    UniqueListItem<ProductionOrderInputMaterial>[]
  >([]);

  // Amount of each batch committed by OTHER orders that haven't discounted yet.
  const [committedByBatch, setCommittedByBatch] = useState<
    Map<number, number>
  >(new Map());

  // Distinct batch ids currently selected in the materials formset.
  const distinctBatchIds = useMemo(() => {
    const ids = new Set<number>();
    for (const material of inputMaterials) {
      const batchId = material.item.inventoryInputBatchId;
      if (batchId !== undefined) {
        ids.add(batchId);
      }
    }
    return Array.from(ids);
  }, [inputMaterials]);

  const distinctBatchIdsKey = distinctBatchIds
    .slice()
    .sort((a, b) => a - b)
    .join(",");

  // Fetch committed-pending amounts whenever the set of selected batches
  // changes. It does not depend on the amounts typed in this form, only on the
  // batches involved, so editing quantities won't re-fetch.
  useEffect(() => {
    let cancelled = false;
    const fetchCommitted = async () => {
      const result = await getCommittedPendingByBatch(
        distinctBatchIds,
        company.id,
        editingOrder.id,
      );
      if (!cancelled) {
        setCommittedByBatch(result);
      }
    };
    fetchCommitted();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctBatchIdsKey, company.id, editingOrder.id]);

  // Derive which rows are inconsistent: a batch repeated within this order, or
  // a batch whose total committed amount (this order + other pending orders)
  // exceeds its available quantity. Both are warnings only, never blocking.
  const { duplicateUuids, overcommittedUuids } = useMemo(() => {
    const rowsByBatch = new Map<
      number,
      UniqueListItem<ProductionOrderInputMaterial>[]
    >();
    for (const material of inputMaterials) {
      const batchId = material.item.inventoryInputBatchId;
      if (batchId === undefined) {
        continue;
      }
      const rows = rowsByBatch.get(batchId) ?? [];
      rows.push(material);
      rowsByBatch.set(batchId, rows);
    }

    const duplicates = new Set<string>();
    const overcommitted = new Set<string>();

    for (const entry of Array.from(rowsByBatch.entries())) {
      const batchId = entry[0];
      const rows = entry[1];

      if (rows.length > 1) {
        for (const row of rows) {
          duplicates.add(row.uuid);
        }
      }

      const batch = rows[0].item.inventoryInputBatch;
      if (batch !== undefined) {
        const available = (batch.amount ?? 0) - (batch.amountUsed ?? 0);
        const committedFromOthers = committedByBatch.get(batchId) ?? 0;
        const remaining = available - committedFromOthers;
        // Mirror the real discount: fixed amounts are taken as-is, per-unit
        // amounts scale with the estimated produced quantity, so changing it
        // re-evaluates the check for non-fixed materials.
        const estimatedAmount = editingOrder.amountProduced ?? 0;
        const sumInForm = rows.reduce((acc, row) => {
          const rowAmount = row.item.amount ?? 0;
          const effectiveAmount = row.item.isFixedAmount
            ? rowAmount
            : rowAmount * estimatedAmount;
          return acc + effectiveAmount;
        }, 0);
        if (sumInForm > remaining) {
          for (const row of rows) {
            overcommitted.add(row.uuid);
          }
        }
      }
    }

    return { duplicateUuids: duplicates, overcommittedUuids: overcommitted };
  }, [inputMaterials, committedByBatch, editingOrder.amountProduced]);

  const [showDetailedCost, setShowDetailedCost] = useState(false);

  const onShowDetailedCostChange = (showDetails: boolean) => {
    setShowDetailedCost(showDetails);
  };

  const onProductChange = (newProduct: Product | null) => {
    setEditingOrder((editingItem) => {
      return {
        ...editingItem,
        productId: newProduct?.id,
        productCodeIdentifier: newProduct?.codeIdentifier,
        productName: newProduct?.name,
        productBrandName: newProduct?.brandName,
        productReferencePrice: newProduct?.price,
        productReferencePriceWholesale: newProduct?.wholesalePrice,
        productReferencePriceSuperWholesale: newProduct?.superWholesalePrice,
      };
    });
    setProduct(newProduct ? newProduct : undefined);
  };

  const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingOrder((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, amountProduced: amount };
    });
  };

  const onAddInputMaterialClick = () => {
    setInputMaterials((inputMaterials) => {
      const newInputMaterials = [
        ...inputMaterials,
        newUniqueListItem<ProductionOrderInputMaterial>({}),
      ];
      return newInputMaterials;
    });
  };

  const onInputMaterialEntryChange = (
    inputMaterial: UniqueListItem<ProductionOrderInputMaterial>,
  ) => {
    setInputMaterials((inputMaterials) => {
      const newInputMaterials = [...inputMaterials];
      const index = newInputMaterials.findIndex((item) => {
        return item.uuid === inputMaterial.uuid;
      });

      if (index === -1) {
        return newInputMaterials;
      }

      newInputMaterials[index] = inputMaterial;

      return newInputMaterials;
    });
  };

  const onInputMaterialEntryDelete = (
    entry: UniqueListItem<ProductionOrderInputMaterial>,
  ) => {
    setInputMaterials((inputMaterials) => {
      const newInputMaterials = [...inputMaterials];

      const index = newInputMaterials.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newInputMaterials;
      }

      newInputMaterials.splice(index, 1);

      return newInputMaterials;
    });
  };

  const onLaborPriceEntryChange = (
    laborPrice: UniqueListItem<ProductionOrderLaborPrice>,
  ) => {
    setLaborPrices((laborPrices) => {
      const newLaborPrices = [...laborPrices];
      const index = newLaborPrices.findIndex((item) => {
        return item.uuid === laborPrice.uuid;
      });

      if (index === -1) {
        return newLaborPrices;
      }

      newLaborPrices[index] = laborPrice;

      return newLaborPrices;
    });
  };

  const calculateLaborCost = (
    laborPricesEnrties: ProductionOrderLaborPrice[],
  ) => {
    const totalLaborPriceCalculation: number = laborPricesEnrties.reduce(
      (sum: number, entry: ProductionOrderLaborPrice) => {
        if (entry.price !== undefined) {
          return sum + entry.price;
        }
        return sum;
      },
      0,
    );
    return totalLaborPriceCalculation;
  };

  const [quickScanBarcode, setQuickScanBarcode] = useState("");

  const onQuickScanKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key !== "Enter" || !quickScanBarcode.trim()) return;
    const result = await getItem<InventoryInputBatch>(
      `/inventory_locations/batches/by_barcode/${quickScanBarcode.trim()}/`,
    );
    setQuickScanBarcode("");
    if (result.status === SUCCESS && result.data) {
      const batch = result.data;
      const defaultAmount = 1;
      const newEntry = newUniqueListItem<ProductionOrderInputMaterial>({
        inventoryInputBatch: batch,
        inventoryInputBatchId: batch.id,
        inputMaterial: batch.inputMaterial,
        inputMaterialId: batch.inputMaterialId,
        inputMaterialIdentifier: batch.inputMaterialIdentifier,
        isFabric: batch.inputMaterial?.type === "fabric",
        amount: defaultAmount,
        isFixedAmount: false,
        // Set price from the start
        price:
          batch.unitPrice != null ? batch.unitPrice * defaultAmount : undefined,
      });
      setInputMaterials((prev) => [...prev, newEntry]);
    } else {
      warningAlert("No se encontró ningún lote con ese código de barra.");
    }
  };

  const calculateMaterialCost = (
    materialEntries: ProductionOrderInputMaterial[],
    amountProduced: number,
  ) => {
    const safeAmount =
      amountProduced > 0 && !isNaN(amountProduced) ? amountProduced : 1;

    return materialEntries.reduce(
      (sum: number, entry: ProductionOrderInputMaterial) => {
        if (entry.price === undefined || entry.price === null) return sum;

        if (entry.isFixedAmount) {
          return sum + entry.price / safeAmount;
        }

        return sum + entry.price;
      },
      0,
    );
  };

  const onSave = async () => {
    setSubmitting(true);

    // Non-blocking consistency warnings: the order is still saved.
    if (duplicateUuids.size > 0) {
      warningAlert(
        "Hay lotes de materia prima repetidos en esta orden. Verifique las " +
          "filas resaltadas.",
      );
    }
    if (overcommittedUuids.size > 0) {
      warningAlert(
        "Hay lotes cuya cantidad total (esta orden más otras órdenes " +
          "pendientes de descontar) supera lo disponible. Verifique las filas " +
          "resaltadas.",
      );
    }

    const totalLaborPrice = 0;
    const totalMaterialPrice = 0;
    let toSendOrder: ProductionOrder = {
      ...editingOrder,
      companyId: company.id,
      orderStatus: "pending",
      expectedProfit: expectedProfit,
      expectedProfitSuperWholesale: expectedProfitSuperWholesale,
      expectedProfitWholesale: expectedProfitWholesale,
      totalCostPrice: totalCostPrice,
      totalLaborPrice: totalLaborPrice,
      totalMaterialPrice: totalMaterialPrice,
      productionMaterials: uniqueListToListItems(inputMaterials),
      productionLaborPrices: uniqueListToListItems(laborPrices),
      remainingAmountToDefine: editingOrder.amountProduced,
    };

    let requestPromise: Promise<ItemRequestStatus<ProductionOrder>>;
    if (editingOrder.id === undefined) {
      requestPromise = createItem<ProductionOrder>(
        "/production/create/",
        toSendOrder,
      );
    } else {
      requestPromise = updateItem<ProductionOrder>(
        `/production/${toSendOrder.id}/`,
        toSendOrder,
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const fetchProduct = async (productId: number) => {
    const productStatus = await getItem<Product>(
      `/clothing_products/${productId}/`,
    );
    if (productStatus.status === SUCCESS) {
      if (productStatus.data !== undefined) {
        setProduct(productStatus.data);
      }
    }
  };

  const getLaborPrices = async () => {
    if (
      initialProductionOrder === undefined ||
      initialProductionOrder.id === undefined
    ) {
      setLaborPrices(await generateLaborPrices());
      return [];
    }
    const productionOrderIdStr = initialProductionOrder.id.toString();
    if (productionOrderIdStr === undefined) {
      setLaborPrices(await generateLaborPrices());
      return [];
    }
    const limit = 100;
    const offset = 0;
    const laborPricesStatus = await getList<ProductionOrderLaborPrice>(
      `/production/${productionOrderIdStr}/labor_prices/`,
      limit,
      offset,
    );
    if (laborPricesStatus.status === SUCCESS) {
      if (laborPricesStatus.data !== undefined) {
        setLaborPrices(listToUniqueListItems(laborPricesStatus.data.items));
      }
    } else {
      setLaborPrices(await generateLaborPrices());
    }
  };

  const getItemsLogs = async () => {
    if (
      initialProductionOrder === undefined ||
      initialProductionOrder.id === undefined
    ) {
      setItemLogs([]);
      return;
    }
    const productionOrderIdStr = initialProductionOrder.id.toString();
    if (productionOrderIdStr === undefined) {
      setItemLogs([]);
      return;
    }
    const limit = 1000;
    const offset = 0;
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", company.id!.toString());
    additionalParams.set("order_id", productionOrderIdStr);
    const itemLogsStatus = await getList<ProductionOrderItemLog>(
      `/production/item_logs/`,
      limit,
      offset,
      additionalParams,
    );
    if (itemLogsStatus.status === SUCCESS) {
      if (itemLogsStatus.data !== undefined) {
        setItemLogs(itemLogsStatus.data.items);
      }
    } else {
      setItemLogs([]);
    }
  };

  const generateLaborPrices = async () => {
    const limit = 100;
    const offset = 0;
    const stagesStatus = await getList<ProductionStage>(
      `/production_stages/`,
      limit,
      offset,
    );
    if (stagesStatus.status === SUCCESS) {
      if (stagesStatus.data !== undefined) {
        const items = stagesStatus.data.items.map((item, ix) => {
          const newItem = newProductionOrderLaborPrice();
          newItem.productionStage = item;
          newItem.stageId = item.id;
          newItem.isActive = false;
          newItem.subject = item.name;
          newItem.price = 0;
          return newItem;
        });
        return listToUniqueListItems(items);
      }
    }
    return [];
  };

  const getInputMaterials = async () => {
    if (
      initialProductionOrder === undefined ||
      initialProductionOrder.id === undefined
    ) {
      setInputMaterials([]);
      return [];
    }
    const productionOrderIdStr = initialProductionOrder.id.toString();
    if (productionOrderIdStr === undefined) {
      setInputMaterials([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const laborPricesStatus = await getList<ProductionOrderInputMaterial>(
      `/production/${productionOrderIdStr}/input_materials/`,
      limit,
      offset,
    );
    if (laborPricesStatus.status === SUCCESS) {
      if (laborPricesStatus.data !== undefined) {
        setInputMaterials(listToUniqueListItems(laborPricesStatus.data.items));
      }
    } else {
      setInputMaterials([]);
    }
  };

  const getOrderSizes = async () => {
    if (
      initialProductionOrder === undefined ||
      initialProductionOrder.id === undefined
    ) {
      setProductionOrderSizes([]);
      return [];
    }
    const productionOrderIdStr = initialProductionOrder.id.toString();
    if (productionOrderIdStr === undefined) {
      setProductionOrderSizes([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const orderSizesStatus = await getList<ProductionOrderSize>(
      `/production/${productionOrderIdStr}/order_sizes/`,
      limit,
      offset,
    );
    if (orderSizesStatus.status === SUCCESS) {
      if (orderSizesStatus.data !== undefined) {
        setProductionOrderSizes(orderSizesStatus.data.items);
      }
    } else {
      setProductionOrderSizes([]);
    }
  };

  const generateTableHeaders = (entries: VariantTableEntry[]) => {
    const uniqueSizeNames: string[] = [];

    entries.forEach((item) => {
      if (item.sizes) {
        item.sizes
          .map((size) => size?.sizeName)
          .filter(
            (sizeName) =>
              sizeName !== undefined && !uniqueSizeNames.includes(sizeName),
          )
          .forEach((sizeName) => {
            if (sizeName) {
              uniqueSizeNames.push(sizeName);
            }
          });
      }
    });

    uniqueSizeNames.sort((a, b) => {
      const aFloat = parseFloat(a);
      const bFloat = parseFloat(b);
      return aFloat - bFloat;
    });

    setTableColumns([...FIXED_COLUMNS, ...uniqueSizeNames]);
  };

  const getOrderItems = async () => {
    if (
      initialProductionOrder === undefined ||
      initialProductionOrder.id === undefined
    ) {
      setTableEntries([]);
      return;
    }
    const productionOrderIdStr = initialProductionOrder.id.toString();
    if (productionOrderIdStr === undefined) {
      setTableEntries([]);
      return;
    }
    const limit = 5000;
    const offset = 0;
    const orderSizesStatus = await getList<ProductionOrderItem>(
      `/production/${productionOrderIdStr}/order_items/`,
      limit,
      offset,
    );
    if (orderSizesStatus.status === SUCCESS) {
      if (orderSizesStatus.data !== undefined) {
        const formattedTableEntries = productionOrderItemAPIToTable(
          orderSizesStatus.data.items,
        );

        setTableEntries(formattedTableEntries);
        generateTableHeaders(formattedTableEntries);
      } else {
        setTableEntries([]);
        return;
      }
    } else {
      setTableEntries([]);
      return;
    }
  };

  const inputMaterialErrors = getFieldErrors(
    "productionMaterials",
    errors,
  ) as Errors[];
  const laborPricesErrors = getFieldErrors("laborPrices", errors) as Errors[];

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingOrder(newProductionOrder());
    setProduct(undefined);
    setEditable(true);
    setInputMaterials([]);
    setLaborPrices([]);
    setProductionOrderSizes([]);
    setTableEntries([]);
    setItemLogs([]);
    setTableColumns(FIXED_COLUMNS);
    setShowDetailedCost(false);
  };

  useEffect(() => {
    const priceitems = calculateMaterialCost(
      uniqueListToListItems(inputMaterials),
      editingOrder.amountProduced ?? 0,
    );

    const priceLabor = calculateLaborCost(uniqueListToListItems(laborPrices));
    const priceSum = priceLabor + priceitems;
    const productPrice = product ? product.price : undefined;
    const priceExpectedProfit = productPrice
      ? productPrice - priceSum
      : undefined;
    setTotalCostPrice(priceitems + priceLabor);
    setExpectedProfit(priceExpectedProfit ? priceExpectedProfit : 0);

    const productPriceWholesale = product ? product.wholesalePrice : undefined;
    const priceExpectedProfitWholesale = productPriceWholesale
      ? productPriceWholesale - priceSum
      : undefined;
    setExpectedProfitWholesale(
      priceExpectedProfitWholesale ? priceExpectedProfitWholesale : 0,
    );

    const productPriceSuperWholesale = product
      ? product.superWholesalePrice
      : undefined;
    const priceExpectedProfitSuperWholesale = productPriceSuperWholesale
      ? productPriceSuperWholesale - priceSum
      : undefined;
    setExpectedProfitSuperWholesale(
      priceExpectedProfitSuperWholesale ? priceExpectedProfitSuperWholesale : 0,
    );

    const newEditingOrderValue = {
      ...editingOrder,
      totalLaborPrice: priceLabor,
      totalMaterialPrice: priceitems,
      totalCostPrice: priceSum,
      expectedProfit: priceExpectedProfit,
      expectedProfitWholesale: priceExpectedProfitWholesale,
      expectedProfitSuperWholesale: priceExpectedProfitSuperWholesale,
    };
    setEditingOrder(newEditingOrderValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborPrices, inputMaterials, product, editingOrder.amountProduced]);

  useEffect(() => {
    setLoading(true);
    setItemLogs([]);
    setEditingOrder(
      initialProductionOrder ? initialProductionOrder : newProductionOrder(),
    );
    if (initialProductionOrder?.productId !== undefined) {
      fetchProduct(initialProductionOrder?.productId);
    } else {
      setProduct(undefined);
    }
    getInputMaterials();
    getLaborPrices();
    getOrderSizes();
    getItemsLogs();
    setEditable(
      orderIsEditable(
        initialProductionOrder ? initialProductionOrder : newProductionOrder(),
      ),
    );
    setLoading(false);
    getOrderItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProductionOrder]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const editableMsg = editable ? (
    <></>
  ) : (
    <small className="mr-2 text-danger">
      La producción ya ha iniciado. Ya no se pueden hacer cambios
    </small>
  );
  const differencelost = editingOrder.amountLost ? editingOrder.amountLost : 0;
  return (
    <>
      <fieldset disabled={submitting || !editable}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Modelo de prenda:</CLabel>
            </CCol>
            <CCol>
              {editable ? (
                <>
                  <ProductSelect
                    value={product ? product : null}
                    onChange={onProductChange}
                  ></ProductSelect>
                  <FieldErrors
                    errors={getFieldErrors("productId", errors) as string[]}
                  ></FieldErrors>
                </>
              ) : (
                <p>
                  {withBrand(
                    `${editingOrder.productCodeIdentifier} - ${editingOrder.productName}`,
                    editingOrder.productBrandName,
                  )}
                </p>
              )}
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Cantidad estimada:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="number"
                placeholder="Cantidad"
                value={
                  editingOrder.amountProduced !== undefined
                    ? editingOrder.amountProduced
                    : 0
                }
                onChange={onAmountChange}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("amountProduced", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {editingOrder.orderStatus === "finished" ? (
          <>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Cantidad defectuosa:</CLabel>
                </CCol>
                <CCol md={3}>
                  <CInput
                    type="number"
                    value={
                      editingOrder.amountLost ? editingOrder.amountLost : 0
                    }
                    disabled={true}
                  ></CInput>
                </CCol>
                <CCol md={1}></CCol>
                <CCol md={3}>
                  <CLabel>Cantidad resultante:</CLabel>
                </CCol>
                <CCol md={3}>
                  <CInput
                    type="number"
                    value={editingOrder.amountProduced! - differencelost}
                    disabled={true}
                  ></CInput>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Observación final:</CLabel>
                </CCol>
                <CCol md={10}>
                  <CTextarea
                    placeholder="-"
                    value={editingOrder.finalObs}
                    disabled={true}
                  ></CTextarea>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        ) : (
          <></>
        )}
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Costo Unitario:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  editingOrder.totalCostPrice !== undefined
                    ? editingOrder.totalCostPrice
                    : 0
                }
              ></CurrencyField>
            </CCol>
            <CCol md={2}>
              <CLabel>Precio Unitario:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={product?.price !== undefined ? product?.price : 0}
              ></CurrencyField>
            </CCol>
            <CCol md={2}>
              <CLabel>Ganancia unitaria:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  editingOrder.expectedProfit !== undefined
                    ? editingOrder.expectedProfit
                    : 0
                }
              ></CurrencyField>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Promedio de Tela(cantidad):</CLabel>
            </CCol>
            <CCol md={2}>
              <CInput
                type="number"
                placeholder="-"
                value={
                  product?.averageFabricUsage !== undefined
                    ? product.averageFabricUsage
                    : undefined
                }
                disabled
              ></CInput>
            </CCol>
            <CCol md={2}>
              <CLabel>Precio Mayorista:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  product?.wholesalePrice !== undefined
                    ? product?.wholesalePrice
                    : 0
                }
              ></CurrencyField>
            </CCol>
            <CCol md={2}>
              <CLabel>Ganancia Mayorista:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  editingOrder.expectedProfitWholesale !== undefined
                    ? editingOrder.expectedProfitWholesale
                    : 0
                }
              ></CurrencyField>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Ancho de Tela:</CLabel>
            </CCol>
            <CCol md={2}>
              <CInput
                type="number"
                placeholder="-"
                value={
                  product?.fabricWidth !== undefined ? product.fabricWidth : 0
                }
                disabled
              ></CInput>
            </CCol>
            <CCol md={2}>
              <CLabel>Precio Supermayorista:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  product?.superWholesalePrice !== undefined
                    ? product?.superWholesalePrice
                    : 0
                }
              ></CurrencyField>
            </CCol>
            <CCol md={2}>
              <CLabel>Ganancia Supermayorista:</CLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={PYG}
                disabled={true}
                value={
                  editingOrder.expectedProfitSuperWholesale !== undefined
                    ? editingOrder.expectedProfitSuperWholesale
                    : 0
                }
              ></CurrencyField>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Ver referencia de Costos:</CLabel>
            </CCol>
            <CCol md={4}>
              <CRow>
                <AnimatedCheckbox
                  value={showDetailedCost}
                  onChange={onShowDetailedCostChange}
                ></AnimatedCheckbox>
              </CRow>
            </CCol>
          </CRow>
        </CFormGroup>
        {showDetailedCost ? (
          <>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Precio promedio de tela:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostFabric}
                    disabled
                  ></CurrencyField>
                </CCol>
                <CCol md={2}>
                  <CLabel>Precio Aseo:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostCleaning}
                    disabled
                  ></CurrencyField>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Precio Corte:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostCutting}
                    disabled
                  ></CurrencyField>
                </CCol>
                <CCol md={2}>
                  <CLabel>Precio Accesorios:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostAccesories}
                    disabled
                  ></CurrencyField>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Precio Confección:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostConfection}
                    disabled
                  ></CurrencyField>
                </CCol>
                <CCol md={2}>
                  <CLabel>Precio Lavado:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostWashing}
                    disabled
                  ></CurrencyField>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Precio Empaquetado:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostStoring}
                    disabled
                  ></CurrencyField>
                </CCol>
                <CCol md={2}>
                  <CLabel>Precio Forro Bolsillo:</CLabel>
                </CCol>
                <CCol md={4}>
                  <CurrencyField
                    currency={PYG}
                    value={product?.productionCostPocketLining}
                    disabled
                  ></CurrencyField>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        ) : (
          <></>
        )}
        <CRow>
          <CCol>
            <CTabs activeTab="laborPrices">
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink data-tab="laborPrices">MANO DE OBRA</CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink data-tab="inputMaterials">MATERIAS PRIMAS</CNavLink>
                </CNavItem>
                {editingOrder.currentStageIsConfection ? (
                  <CNavItem>
                    <CNavLink data-tab="sizes">TALLES CORTADOS</CNavLink>
                  </CNavItem>
                ) : (
                  <></>
                )}
                {tableEntries.length > 0 ? (
                  <>
                    <CNavItem>
                      <CNavLink data-tab="items">
                        COLORES Y TALLES PRODUCIDOS
                      </CNavLink>
                    </CNavItem>
                    <CNavItem>
                      <CNavLink data-tab="logs">
                        Registro de Finalización
                      </CNavLink>
                    </CNavItem>
                  </>
                ) : (
                  <></>
                )}
              </CNav>
              <CTabContent>
                <CTabPane data-tab="laborPrices">
                  {laborPrices.map((laborPrice, ix) => {
                    return (
                      <CRow key={laborPrice.uuid}>
                        <CCol>
                          <ProductionLaborPriceForm
                            value={laborPrice.item}
                            initialStage={laborPrice.item.productionStage}
                            initialPrice={laborPrice.item.price}
                            onChange={(item) =>
                              onLaborPriceEntryChange({
                                uuid: laborPrice.uuid,
                                item: item,
                              })
                            }
                            errors={
                              ix < laborPricesErrors.length
                                ? laborPricesErrors[ix]
                                : {}
                            }
                          />
                        </CCol>
                      </CRow>
                    );
                  })}
                </CTabPane>
                <CTabPane data-tab="inputMaterials">
                  <CRow className="mb-3 mt-3 align-items-center">
                    <CCol md={2} className="mt-2">
                      <strong>Agregar por código:</strong>
                    </CCol>
                    <CCol md={4}>
                      <CInput
                        type="text"
                        placeholder="Escanee código de barra para agregar lote..."
                        value={quickScanBarcode}
                        disabled={!editable}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setQuickScanBarcode(e.target.value)
                        }
                        onKeyDown={onQuickScanKeyDown}
                      />
                    </CCol>
                    <CCol md={4}>
                      <small className="text-muted">
                        Presione Enter tras escanear para agregar el lote
                        automáticamente.
                      </small>
                    </CCol>
                  </CRow>
                  {inputMaterials.map((inputMaterial, ix) => {
                    return (
                      <CRow key={inputMaterial.uuid}>
                        <CCol>
                          <ProductionInputMaterialForm
                            value={inputMaterial.item}
                            onDelete={() =>
                              onInputMaterialEntryDelete(inputMaterial)
                            }
                            initialAmount={inputMaterial.item.amount}
                            initialBatch={
                              inputMaterial.item.inventoryInputBatch
                            }
                            disabled={!editable}
                            committedFromOthers={
                              inputMaterial.item.inventoryInputBatchId !==
                              undefined
                                ? committedByBatch.get(
                                    inputMaterial.item.inventoryInputBatchId,
                                  ) ?? 0
                                : 0
                            }
                            isDuplicateInOrder={duplicateUuids.has(
                              inputMaterial.uuid,
                            )}
                            isOvercommitted={overcommittedUuids.has(
                              inputMaterial.uuid,
                            )}
                            onChange={(item) =>
                              onInputMaterialEntryChange({
                                uuid: inputMaterial.uuid,
                                item: item,
                              })
                            }
                            errors={
                              ix < inputMaterialErrors.length
                                ? inputMaterialErrors[ix]
                                : {}
                            }
                          />
                        </CCol>
                      </CRow>
                    );
                  })}
                  {inputMaterials.length === 0 ? (
                    <CRow>
                      <CCol>
                        Agregue nuevas materias primas con el boton + de la
                        derecha
                      </CCol>
                    </CRow>
                  ) : (
                    <></>
                  )}
                  <CRow className="mb-1">
                    <CCol>
                      <CButton
                        className="btn btn-primary float-right"
                        onClick={onAddInputMaterialClick}
                        disabled={!editable}
                      >
                        <i className="fa fa-plus" />
                      </CButton>
                    </CCol>
                  </CRow>
                </CTabPane>
                {editingOrder.currentStageIsConfection ? (
                  <CTabPane data-tab="sizes">
                    {productionOrderSizes.map((orderSize, ix) => {
                      return (
                        <CRow key={ix}>
                          <CCol md={1} className={"offset-md-1"}>
                            <CLabel>Talle:</CLabel>
                          </CCol>
                          <CCol md={2}>
                            <p>{orderSize.sizeName}</p>
                          </CCol>
                          <CCol md={1}>
                            <CLabel>Cantidad:</CLabel>
                          </CCol>
                          <CCol md={2}>
                            <p>{orderSize.amount}</p>
                          </CCol>
                        </CRow>
                      );
                    })}
                  </CTabPane>
                ) : (
                  <></>
                )}
                {tableEntries.length > 0 ? (
                  <>
                    <CTabPane data-tab="items">
                      <CRow>
                        <CCol md={12}>
                          <div className="table-responsive">
                            <table className="table table-striped table-bordered table-fixed">
                              <thead>
                                <tr>
                                  {tableColumns.map((title, ix) => {
                                    return (
                                      <th
                                        className="text-center"
                                        key={ix}
                                        style={{
                                          verticalAlign: "middle",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div className="d-inline">{title}</div>
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {tableEntries.map((tableEntry, ix) => {
                                  return (
                                    <tr
                                      style={{
                                        verticalAlign: "middle",
                                        overflow: "hidden",
                                      }}
                                      key={ix}
                                    >
                                      <td
                                        className={"text-center"}
                                        style={{ padding: 0, width: "120px" }}
                                      >
                                        <p>{tableEntry.colorName}</p>
                                      </td>
                                      {tableColumns.map((column, ix) => {
                                        if (column === "Color") {
                                          return <></>;
                                        }
                                        const matchSize =
                                          tableEntry.sizes?.find(
                                            (item) => item.sizeName === column,
                                          );
                                        const disabled =
                                          matchSize === undefined;
                                        let showValue = "";
                                        if (matchSize !== undefined) {
                                          if (
                                            matchSize.finishedAmount !==
                                            undefined
                                          ) {
                                            showValue = `${matchSize.finishedAmount}/${matchSize.totalAmount}`;
                                          } else {
                                            showValue = `${matchSize.totalAmount}`;
                                          }
                                        }
                                        return (
                                          <td
                                            key={ix}
                                            style={{
                                              padding: 0,
                                              width: "70px",
                                            }}
                                          >
                                            <CInput
                                              type="text"
                                              value={
                                                disabled
                                                  ? ""
                                                  : matchSize
                                                    ? emptyValueOnUndefined(
                                                        showValue,
                                                      )
                                                    : ""
                                              }
                                              disabled={true}
                                            ></CInput>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                                {tableEntries.length === 0 ? (
                                  <tr>
                                    <td colSpan={tableColumns.length}>
                                      No hay tamaños disponibles para esta
                                      prenda.
                                    </td>
                                  </tr>
                                ) : (
                                  <></>
                                )}
                              </tbody>
                            </table>
                            <br />
                            <br />
                          </div>
                        </CCol>
                      </CRow>
                    </CTabPane>
                    <CTabPane data-tab="logs">
                      <CRow>
                        <CCol md={12}>
                          <div>
                            <CDataTable
                              noItemsView={
                                <h2 className="text-center">Sin Resultados</h2>
                              }
                              addTableClasses={"table-fixed"}
                              fields={fields}
                              items={itemLogs}
                              striped
                              border
                              loading={loading}
                              responsive
                              scopedSlots={{
                                createdAt: (item: ProductionOrderItemLog) => {
                                  return (
                                    <td className="text-center">
                                      {item.createdAt
                                        ? formatDateTime(
                                            new Date(item.createdAt),
                                          )
                                        : "-"}
                                    </td>
                                  );
                                },
                                productName: (item: ProductionOrderItemLog) => {
                                  return (
                                    <td className="text-center">
                                      {item.productName}
                                    </td>
                                  );
                                },
                                registerType: (
                                  item: ProductionOrderItemLog,
                                ) => {
                                  return (
                                    <td className="text-center">
                                      {item.registerType === "finished"
                                        ? "Finalización"
                                        : "Fallado"}
                                    </td>
                                  );
                                },
                                amount: (item: ProductionOrderItemLog) => {
                                  return (
                                    <td className="text-center">
                                      {item.amount}
                                    </td>
                                  );
                                },
                                user: (item: ProductionOrderItemLog) => {
                                  return (
                                    <td className="text-center">
                                      {defaultValueOnUndefined(
                                        "No Disponible",
                                        item.user,
                                      )}
                                    </td>
                                  );
                                },
                              }}
                            />
                          </div>
                        </CCol>
                      </CRow>
                    </CTabPane>
                  </>
                ) : (
                  <></>
                )}
              </CTabContent>
            </CTabs>
          </CCol>
        </CRow>
        <CFormGroup className="float-right">
          {editableMsg}
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default ProductionForm;
